/* CUSTOM [MTO / Gift wrap]: Product form behavior. Liquid supplies configuration in buy-buttons.liquid. */
(() => {
  // Serialize additions across the product forms on this page until stock is refreshed.
  const stockClients = new Set();
  let additionPending = false;
  function beginAddition() {
    additionPending = true;
    stockClients.forEach((client) => client.invalidate());
  }
  async function finishAddition() {
    try {
      await Promise.all([...stockClients].map((client) => client.refresh()));
    } finally {
      additionPending = false;
      stockClients.forEach((client) => client.update());
    }
  }

  function initializeProduct(config, lifecycle) {
    const { sectionId, productFormId, mto } = config;
    const giftWrapOptions = new Map(
      config.giftWrapOptions.map((option) => [String(option.variantId), option])
    );

    async function initMadeToOrder() {

      const variantDataElement =
        document.getElementById(
          `MadeToOrderVariants-${sectionId}`
        );

      if (!variantDataElement) return;


      let variants;

      try {

        variants =
          JSON.parse(
            variantDataElement.textContent
          );

      } catch (error) {

        console.error(
          'Made to Order variant data could not be parsed.',
          error
        );

        return;
      }


      const form = lifecycle.parentElement;

      if (!form) return;


      if (
        form.dataset.mtoInitialized === 'true'
      ) {
        return;
      }

      form.dataset.mtoInitialized = 'true';
      const controller = new AbortController();
      const { signal } = controller;
      let observer;
      let unsubscribeCartUpdate;
      let animationFrame;
      let stockClient;
      let retryPanel;
      lifecycle.cleanup = () => {
        controller.abort();
        stockClients.delete(stockClient);
        retryPanel?.remove();
        observer?.disconnect();
        unsubscribeCartUpdate?.();
        cancelAnimationFrame(animationFrame);
        delete form.dataset.mtoInitialized;
      };
      const listen = (target, type, callback, capture = false) =>
        target.addEventListener(type, callback, { capture, signal });


      const variantInput =
        form.querySelector(
          'input[name="id"].product-variant-id'
        );

      const submitButton =
        form.querySelector(
          '[name="add"]'
        );

      const buttonText =
        form.querySelector(
          '[data-mto-button-text]'
        );

      const readyAvailabilityInput =
        form.querySelector(
          '[data-ready-availability]'
        );

      const orderTypeInput =
        form.querySelector(
          '[data-mto-order-type]'
        );

      const productionTimeInput =
        form.querySelector(
          '[data-mto-production-time]'
        );

      const message =
        form.querySelector(
          '[data-mto-message]'
        );

      const mixedMessage =
        form.querySelector(
          '[data-mto-mixed-message]'
        );

      const dynamicCheckout =
        form.querySelector(
          '[data-mto-dynamic-checkout]'
        );

      const standardMtoDetails =
        document.getElementById(
          `CustomStandardMtoDetails-${sectionId}`
        );


      const quantityInput =
        document.querySelector(
          `input[name="quantity"][form="${productFormId}"]`
        )
        ||
        form.querySelector(
          'input[name="quantity"]'
        );

      /*
        CUSTOM [Gift wrap]: gift-wrap configuration
      */
      const giftWrapInputs =
        form.querySelectorAll(
          `input[name="gift-wrap-${sectionId}"]`
        );

      const giftMessageWrapper =
        document.getElementById(
          `GiftMessageWrapper-${sectionId}`
        );

      const giftMessageInput =
        document.getElementById(
          `GiftMessage-${sectionId}`
        );


      if (
        !variantInput ||
        !submitButton ||
        !buttonText ||
        !readyAvailabilityInput ||
        !orderTypeInput ||
        !productionTimeInput
      ) {
        return;
      }


      const addToCartText =
        config.strings.addToCart;

      const soldOutText =
        config.strings.soldOut;

      const unavailableText =
        config.strings.unavailable;


      /*
        CUSTOM [MTO]: Cart cache
      */

      let readyQuantitiesInCart = {};
      let cartStateLoaded = false;
      let cartStateFailed = false;
      let refreshVersion = 0;

      retryPanel = document.createElement('div');
      retryPanel.className = 'availability-retry-message';
      retryPanel.hidden = true;
      const retryMessage = document.createElement('p');
      retryMessage.setAttribute('role', 'alert');
      retryMessage.textContent = 'Unable to check availability. Please retry before adding this item.';
      retryPanel.append(retryMessage);
      form.appendChild(retryPanel);


      async function loadCartState(version) {

        const response =
          await fetch(
            `${window.Shopify.routes.root}cart.js`,
            {
              signal,
              cache: 'no-store',
              headers: {
                Accept: 'application/json'
              }
            }
          );


        if (!response.ok) {
          throw new Error(
            'Unable to read cart.'
          );
        }


        const cart =
          await response.json();


        if (signal.aborted || version !== refreshVersion) return;
        if (!Array.isArray(cart.items)) throw new Error('Invalid cart response.');

        readyQuantitiesInCart = {};


        cart.items.forEach(
          (item) => {

            const variantId =
              String(
                item.variant_id
              );

            const isMtoLine =
              item.properties &&
              item.properties[mto.order_type_property] ===
                mto.made_to_order;


            if (isMtoLine) {
              return;
            }


            readyQuantitiesInCart[variantId] =
              (
                readyQuantitiesInCart[variantId]
                || 0
              )
              +
              item.quantity;
          }
        );


        cartStateLoaded = true;
      }

      async function refreshCartCache() {
        if (signal.aborted || !productHasMadeToOrderVariant) return;
        const version = ++refreshVersion;
        cartStateLoaded = false;
        cartStateFailed = false;
        retryPanel.hidden = true;
        updateMadeToOrderState();
        try {
          await loadCartState(version);
        } catch (error) {
          if (signal.aborted || version !== refreshVersion) return;
          cartStateLoaded = false;
          cartStateFailed = true;
        } finally {
          if (!signal.aborted && version === refreshVersion) {
            updateMadeToOrderState();
          }
        }
      }

      /*
        CUSTOM [MTO]: Quantity
      */

      function getRequestedQuantity() {

        return Math.max(
          1,
          parseInt(
            quantityInput?.value || '1',
            10
          ) || 1
        );
      }

      /*
        CUSTOM [Gift wrap]: read the selected gift-wrap option
      */
      function getSelectedGiftWrap() {

        const selectedInput =
          form.querySelector(
            `input[name="gift-wrap-${sectionId}"]:checked`
          );

        const giftWrapId =
          String(
            selectedInput?.value || ''
          );

        if (!giftWrapId) {
          return null;
        }

        return (
          giftWrapOptions.get(
            giftWrapId
          ) || null
        );
      }


      function getGiftMessage() {

        return (
          giftMessageInput?.value?.trim()
          || ''
        );
      }


      function updateGiftWrapUI() {

        const selectedGiftWrap =
          getSelectedGiftWrap();

        if (giftMessageWrapper) {
          giftMessageWrapper.style.display =
            selectedGiftWrap
              ? 'block'
              : 'none';
        }

        if (
          !selectedGiftWrap &&
          giftMessageInput
        ) {
          giftMessageInput.value = '';
        }
      }

      /*
        CUSTOM [MTO]: MTO eligibility
      */
      function supportsMadeToOrder(
        variant
      ) {

        return (
          variant &&
          variant.made_to_order === true &&
          variant.inventory_management ===
            'shopify' &&
          variant.inventory_policy ===
            'continue'
        );
      }

      const productHasMadeToOrderVariant =
        Object.values(
          variants
        ).some(
          (variant) =>
            supportsMadeToOrder(
              variant
            )
        );

      /*
        CUSTOM [MTO]: Ready stock remaining
      */

      function getReadyRemaining(
        variantId,
        variant
      ) {

        const physicalInventory =
          Math.max(
            0,
            Number(
              variant.inventory_quantity
            )
          );


        const alreadyInCart =
          readyQuantitiesInCart[
            String(variantId)
          ]
          || 0;


        return Math.max(
          physicalInventory -
            alreadyInCart,
          0
        );
      }


      /*
        CUSTOM [MTO]: Synchronous UI update
      */

      function updateMadeToOrderState() {
        if (signal.aborted) return;
        // Preserve property inputs while the native form constructs its FormData.
        if (additionPending) {
          submitButton.disabled = true;
          if (dynamicCheckout) dynamicCheckout.hidden = true;
          return;
        }
        retryPanel.hidden = !cartStateFailed || !supportsMadeToOrder(variants[String(variantInput.value)]);

        const variantId =
          String(
            variantInput.value || ''
          );


        const variant =
          variants[variantId];


        if (standardMtoDetails) {
          standardMtoDetails.hidden =
            variant?.made_to_order !== true;
        }


        if (message) {
          message.hidden = true;
        }


        if (mixedMessage) {
          mixedMessage.hidden = true;
          mixedMessage.textContent = '';
        }


        /*
          No valid variant.
        */
        if (!variant) {

          buttonText.textContent =
            unavailableText;

          submitButton.disabled = true;

          readyAvailabilityInput.disabled =
            true;

          orderTypeInput.disabled =
            true;

          productionTimeInput.disabled =
            true;

          if (dynamicCheckout) {
            dynamicCheckout.hidden = true;
          }

          return;
        }


        const canMakeToOrder =
          supportsMadeToOrder(
            variant
          );


        /*
          MTO-capable product still waiting for
          cart state.
        */
        if (
          canMakeToOrder &&
          !cartStateLoaded
        ) {

          buttonText.textContent =
            cartStateFailed ? 'RETRY AVAILABILITY' : 'CHECKING AVAILABILITY…';

          submitButton.disabled = !cartStateFailed;

          readyAvailabilityInput.disabled =
            true;

          orderTypeInput.disabled =
            true;

          productionTimeInput.disabled =
            true;

          if (dynamicCheckout) {
            dynamicCheckout.hidden = true;
          }

          return;
        }


        /*
          CUSTOM [MTO]: MTO-capable variant
        */

        if (canMakeToOrder) {

          const requested =
            getRequestedQuantity();


          const readyRemaining =
            getReadyRemaining(
              variantId,
              variant
            );


          /*
            ----------------------------------
            FULLY MADE TO ORDER
            ----------------------------------
          */

          if (
            readyRemaining === 0
          ) {

            buttonText.textContent =
              'Made to order';


            submitButton.disabled =
              false;

            variantInput.disabled =
              false;


            /*
              IMPORTANT:

              Do not submit Ready-to-Ship property
              for a fully MTO purchase.
            */
            readyAvailabilityInput.disabled =
              true;


            orderTypeInput.disabled =
              false;

            productionTimeInput.disabled =
              false;


            if (message) {
              message.hidden = false;
            }


            if (dynamicCheckout) {
              dynamicCheckout.hidden = true;
            }


            return;
          }


          /*
            ----------------------------------
            MIXED READY + MTO
            ----------------------------------
          */

          if (
            requested >
            readyRemaining
          ) {

            const readyQuantity =
              readyRemaining;


            const mtoQuantity =
              requested -
              readyQuantity;


            buttonText.textContent =
              addToCartText;


            submitButton.disabled =
              false;

            variantInput.disabled =
              false;


            /*
              Native form isn't used for the split,
              but keep the Ready state consistent.
            */
            readyAvailabilityInput.disabled =
              false;


            orderTypeInput.disabled =
              true;

            productionTimeInput.disabled =
              true;


            if (mixedMessage) {

              mixedMessage.textContent =
                `${readyQuantity} ready to ship · ` +
                `${mtoQuantity} made to order · ` +
                `Made-to-order pieces ship in approximately ${mto.production_time}.`;


              mixedMessage.hidden =
                false;
            }


            if (dynamicCheckout) {
              dynamicCheckout.hidden = true;
            }


            return;
          }


          /*
            ----------------------------------
            COMPLETELY READY TO SHIP
            ----------------------------------
          */

          buttonText.textContent =
            addToCartText;


          submitButton.disabled =
            false;

          variantInput.disabled =
            false;


          /*
            Native Studio Add to Cart now submits
            this property as well.

            This makes native Ready items merge
            with Ready items created by our mixed
            AJAX split.
          */
          readyAvailabilityInput.disabled =
            false;


          orderTypeInput.disabled =
            true;

          productionTimeInput.disabled =
            true;


          if (dynamicCheckout) {
            dynamicCheckout.hidden = true;
          }


          return;
        }


        /*
          ======================================
          NORMAL NON-MTO VARIANT
          ======================================

          Available non-MTO products are ready to ship.
          Submit the same availability property used by
          ready lines of MTO-capable products so cart
          presentation stays consistent.
        */

        readyAvailabilityInput.disabled =
          !variant.available;

        orderTypeInput.disabled =
          true;

        productionTimeInput.disabled =
          true;


        if (variant.available) {

          buttonText.textContent =
            addToCartText;

          submitButton.disabled =
            false;

          variantInput.disabled =
            false;


          if (dynamicCheckout) {
            dynamicCheckout.hidden =
              Boolean(
                getSelectedGiftWrap()
              );
          }

        } else {

          buttonText.textContent =
            soldOutText;

          submitButton.disabled =
            true;


          if (dynamicCheckout) {
            dynamicCheckout.hidden = true;
          }
        }
      }


      /*
        CUSTOM [MTO]: Variant changes
      */

      if (signal.aborted) return;

      observer =
        new MutationObserver(
          () => {

            updateMadeToOrderState();

          }
        );


      observer.observe(
        variantInput,
        {
          attributes: true,
          attributeFilter: ['value']
        }
      );


      listen(variantInput,
        'change',
        updateMadeToOrderState
      );


      listen(document,
        'change',
        () => {

          cancelAnimationFrame(animationFrame);
          animationFrame = requestAnimationFrame(
            updateMadeToOrderState
          );

        }
      );


      /*
        CUSTOM [MTO]: Quantity changes
      */

      if (quantityInput) {

        listen(quantityInput,
          'input',
          updateMadeToOrderState
        );


        listen(quantityInput,
          'change',
          updateMadeToOrderState
        );
      }

      /*
        CUSTOM [MTO]: Gift wrap changes
      */

      giftWrapInputs.forEach(
        (input) => {

          listen(input,
            'change',
            updateGiftWrapUI
          );

        }
      );

      updateGiftWrapUI();

      // Studio publishes this after successful cart adds and quantity changes.
      // Return the refresh promise so publishers can await this subscriber.
      if (productHasMadeToOrderVariant) {
        unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, refreshCartCache);
      }

      /*
        Backup:

        If cart contents changed elsewhere and the
        customer returns to this page/tab, refresh.
      */
      if (productHasMadeToOrderVariant) {
        listen(window, 'focus', refreshCartCache);
      }

      /*
        CUSTOM [MTO]: Add to cart
      */

      listen(form,
        'submit',

        async (event) => {
          const selectedVariant = variants[String(variantInput.value || '')];
          // Retry consumes this submission; it must never fall through to cart/add.
          if (!additionPending && supportsMadeToOrder(selectedVariant) && cartStateFailed) {
            event.preventDefault();
            event.stopImmediatePropagation();
            await refreshCartCache();
            return;
          }
          if (additionPending || submitButton.getAttribute('aria-disabled') === 'true' ||
              (supportsMadeToOrder(selectedVariant) && !cartStateLoaded)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            updateMadeToOrderState();
            return;
          }

          const variantId =
            String(
              variantInput.value || ''
            );


          const variant =
            variants[variantId];


          if (!variant) {
            return;
          }


          const selectedGiftWrap =
            getSelectedGiftWrap();


          const requestedQuantity =
            getRequestedQuantity();


          const canMakeToOrder =
            supportsMadeToOrder(
              variant
            );


          /*
            Determine whether this is a mixed
            Ready + Made-to-Order purchase.
          */
          let readyRemaining = 0;
          let isMixedOrder = false;


          if (canMakeToOrder) {

            readyRemaining =
              getReadyRemaining(
                variantId,
                variant
              );


            isMixedOrder =
              readyRemaining > 0 &&
              requestedQuantity >
                readyRemaining;
          }


          /*
            No gift wrap + no Ready/MTO split:
            leave Studio's native Add to Cart
            completely untouched.
          */
          if (
            !selectedGiftWrap &&
            !isMixedOrder
          ) {
            beginAddition();
            // Keep this one-shot listener through removal: the in-flight native
            // request must release the shared lock even if its form is detached.
            form.addEventListener('product-form:submit-end', finishAddition, { once: true });
            return;
          }


          /*
            We need to submit multiple cart lines
            ourselves from this point onward.
          */
          event.preventDefault();

          event.stopImmediatePropagation();


          const items = [];


          /*
            Preserve other line-item properties
            already present on the product form.
          */
          const commonProperties = {};


          const formData =
            new FormData(
              form
            );


          for (
            const [name, value]
            of formData.entries()
          ) {

            const match =
              name.match(
                /^properties\[(.+)\]$/
              );


            if (!match) {
              continue;
            }


            const propertyName =
              match[1];


            /*
              These MTO properties are rebuilt
              below for the appropriate cart line.
            */
            if (
              propertyName ===
                mto.availability_property ||
              propertyName ===
                mto.order_type_property ||
              propertyName ===
                mto.production_time_property
            ) {

              continue;
            }


            if (
              String(value).trim() !== ''
            ) {

              commonProperties[
                propertyName
              ] = value;
            }
          }


          const giftMessage =
            getGiftMessage();


          /*
            Build properties for a ceramic line.
          */
          function buildCeramicProperties(
            extraProperties = {}
          ) {

            const properties = {
              ...commonProperties,
              ...extraProperties
            };


            if (selectedGiftWrap) {

              properties['Gift Wrap'] =
                selectedGiftWrap.title;


              if (giftMessage) {

                properties['Gift Message'] =
                  giftMessage;
              }

              properties['_gift_wrap_variant'] =
                String(
                  selectedGiftWrap.variantId
                );
            }


            return properties;
          }


          let readyQuantityAdded = 0;


          /*
            CUSTOM [MTO]: Normal non-mto product
          */

          if (!canMakeToOrder) {

            items.push({
              id:
                Number(
                  variantId
                ),

              quantity:
                requestedQuantity,

              properties:
                buildCeramicProperties({
                  [mto.availability_property]:
                    mto.ready
                })
            });

          }


          /*
            CUSTOM [MTO]: Completely made to order
          */

          else if (
            readyRemaining === 0
          ) {

            items.push({
              id:
                Number(
                  variantId
                ),

              quantity:
                requestedQuantity,

              properties:
                buildCeramicProperties({
                  [mto.order_type_property]:
                    mto.made_to_order,

                  [mto.production_time_property]:
                    mto.production_time
                })
            });

          }


          /*
            CUSTOM [MTO]: Completely ready to ship
          */

          else if (
            requestedQuantity <=
              readyRemaining
          ) {

            items.push({
              id:
                Number(
                  variantId
                ),

              quantity:
                requestedQuantity,

              properties:
                buildCeramicProperties({
                  [mto.availability_property]:
                    mto.ready
                })
            });


            readyQuantityAdded =
              requestedQuantity;

          }


          /*
            CUSTOM [MTO]: Mixed ready + made to order
          */

          else {

            const readyQuantity =
              Math.min(
                requestedQuantity,
                readyRemaining
              );


            const mtoQuantity =
              requestedQuantity -
              readyQuantity;


            if (
              readyQuantity > 0
            ) {

              items.push({
                id:
                  Number(
                    variantId
                  ),

                quantity:
                  readyQuantity,

                properties:
                  buildCeramicProperties({
                    [mto.availability_property]:
                      mto.ready
                  })
              });


              readyQuantityAdded =
                readyQuantity;
            }


            if (
              mtoQuantity > 0
            ) {

              items.push({
                id:
                  Number(
                    variantId
                  ),

                quantity:
                  mtoQuantity,

                properties:
                  buildCeramicProperties({
                    [mto.order_type_property]:
                      mto.made_to_order,

                    [mto.production_time_property]:
                      mto.production_time
                  })
              });
            }
          }


          /*
            CUSTOM [MTO]: Gift wrapping product
          */
          if (selectedGiftWrap) {

            items.push({
              id:
                Number(
                  selectedGiftWrap.variantId
                ),

              quantity:
                requestedQuantity
            });
          }


          if (
            items.length === 0
          ) {

            return;
          }


          beginAddition();
          try {

            submitButton.disabled =
              true;


            submitButton.classList.add(
              'loading'
            );


            const response =
              await fetch(
                `${window.Shopify.routes.root}cart/add.js`,
                {
                  method:
                    'POST',

                  headers: {
                    'Content-Type':
                      'application/json',

                    Accept:
                      'application/json'
                  },

                  body:
                    JSON.stringify({
                      items: items
                    })
                }
              );


            if (!response.ok) {

              const result =
                await response.json();


              throw new Error(
                result.description ||
                result.message ||
                'Unable to add item to cart.'
              );
            }


            /*
              Update Ready-to-Ship cache.
            */
            if (
              readyQuantityAdded > 0
            ) {

              readyQuantitiesInCart[
                variantId
              ] =
                (
                  readyQuantitiesInCart[
                    variantId
                  ]
                  || 0
                )
                +
                readyQuantityAdded;
            }


            /*
              For now, show the full cart after
              a multi-line add.
            */
            window.location.href =
              `${window.Shopify.routes.root}cart`;


          } catch (error) {

            console.error(
              error
            );


            submitButton.disabled =
              false;


            submitButton.classList.remove(
              'loading'
            );


            showMtoError(
              error.message ||
              'Something went wrong. Please try again.'
            );
            // A lost response may still have added items. Read the cart before
            // allowing another submission instead of trusting the previous totals.
            await finishAddition();
          }
        },

        true
      );


      /*
        CUSTOM [MTO]: Error helper
      */

      function showMtoError(
        text
      ) {

        const wrapper =
          form
            .closest(
              'product-form'
            )
            ?.querySelector(
              '.product-form__error-message-wrapper'
            );


        const errorMessage =
          wrapper
            ?.querySelector(
              '.product-form__error-message'
            );


        if (
          wrapper &&
          errorMessage
        ) {

          errorMessage.textContent =
            text;

          wrapper.hidden =
            false;
        }
      }
      stockClient = {
        refresh: refreshCartCache,
        update: updateMadeToOrderState,
        invalidate() {
          ++refreshVersion;
          cartStateLoaded = false;
          updateMadeToOrderState();
        },
      };
      stockClients.add(stockClient);
      if (productHasMadeToOrderVariant) {
        await refreshCartCache();
      } else {
        cartStateLoaded = true;
        updateMadeToOrderState();
      }
    }


    initMadeToOrder();
  }

  // A child element ties resource ownership to the actual form DOM lifecycle,
  // including quick-add replacement and theme-editor section removal.
  customElements.define('custom-mto-lifecycle', class extends HTMLElement {
    connectedCallback() {
      if (this.config) initializeProduct(this.config, this);
    }

    disconnectedCallback() {
      this.cleanup?.();
      this.cleanup = null;
    }
  });

  function mountProduct(config) {
    const form = document.getElementById(config.productFormId);
    if (!form || form.querySelector('custom-mto-lifecycle')) return;
    const lifecycle = document.createElement('custom-mto-lifecycle');
    lifecycle.hidden = true;
    lifecycle.config = config;
    form.appendChild(lifecycle);
  }

  function registerProduct(config) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => mountProduct(config), { once: true });
    } else {
      mountProduct(config);
    }
  }

  // Product snippets can render before this deferred asset or later in a quick-add modal.
  window.initializeMadeToOrderProduct = registerProduct;
  const pendingConfigs = window.madeToOrderProductConfigs || [];
  window.madeToOrderProductConfigs = [];
  pendingConfigs.forEach(registerProduct);
})();
